import * as React from 'react';
import { useQuery } from '@apollo/client';
import { GET_VIEW } from '../../graphql/queries';

import { Filter, FacetUnion, Facet } from '../../fragments/types';
import { ViewRequest, DataFormat, Entity } from '../../graphql/generated/graphql';
import styles from './facets.module.scss';
export interface FacetsType {
  type: string;
  ids: string[] | number[];
  labels?: Record<string, string>;
}

export function createFacets(filters?: Filter, facets?: FacetUnion[]): FacetsType[] {
  if (!(filters && facets)) {
    return [];
  }

  return facets.reduce((result: FacetsType[], facet: FacetUnion) => {
    const entity = typeof facet === 'string' ? facet : (facet as Facet).entity;
    const filterIds = filters[entity as string];
    const ids = Array.isArray(filterIds) ? filterIds : [];
    if (ids.length > 1) {
      result.push({
        type: entity as string,
        ids,
        labels: (facet as Facet).labels,
      });
    }
    return result;
  }, []);
}

function createGraphqlFacetRequest(mainEntity: string, facet: FacetsType, pageFilters?: Filter): ViewRequest {
  const measureName = `${mainEntity}Count`;
  return {
    measures: [
      {
        measureName,
      },
    ],
    entities: [
      {
        entity: facet.type,
        selection: 'individual',
        sortBy: {
          measureName,
        },
        pageNumber: 1,
        entityIds: facet.ids as string[],
      },
    ],
    dataFormat: DataFormat.Single,
    pageFilters,
  };
}

interface PillFromEntities {
  id: string;
  name: string;
  count?: number;
}
const createPillsFromEntities = (entities: Entity[], labels?: Record<string, string>): PillFromEntities[] => {
  return entities.map((entity) => ({
    id: entity.id,
    name: labels?.[entity.id] ?? entity.name,
    count: JSON.parse(entity.dataJson)[0][0],
  }));
};
const Facets = React.memo(
  ({
    mainEntity,
    facet,
    filters,
    selectedId,
    onClick,
  }: {
    mainEntity: string;
    facet: FacetsType;
    selectedId: string;
    onClick: (id: string | number) => void;
    filters?: Filter;
  }) => {
    const request = createGraphqlFacetRequest(mainEntity, facet, filters);
    const { data } = useQuery(GET_VIEW, {
      variables: {
        request,
      },
      fetchPolicy: 'no-cache',
    });

    const entities = data?.view?.entitiesResponse[0].entities || [];
    const pills = [{ name: 'All', id: 'all' }, ...createPillsFromEntities(entities, facet.labels)];
    return (
      <ul className={` ul--horizontal`}>
        {pills.map((pill) => (
          <Pill
            key={pill.name}
            name={pill.name}
            selected={selectedId === pill.id}
            count={pill.count}
            entityId={pill.id}
            onClick={onClick}
          />
        ))}
      </ul>
    );
  },
);
Facets.displayName = 'Facets';

const Pill = React.memo(
  ({
    name,
    count,
    entityId,
    onClick,
    selected,
  }: {
    name: string;
    entityId: string;
    selected: boolean;
    onClick: (entityId: string | number) => void;
    count?: number;
  }) => {
    const badgeRef = React.useRef(null);

    React.useLayoutEffect(() => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      badgeRef.current.onClick = () => onClick(entityId);
    }, [onClick, entityId]);
    const countValue = count ? ` (${count})` : '';
    return (
      <li key={entityId} className={styles.root}>
        <els-badge
          data-testid={entityId}
          ref={badgeRef}
          class={selected ? styles.selected : styles.normal}
          label={`${name}${countValue}`}
          clickable
          small
        />
      </li>
    );
  },
);
Pill.displayName = 'Pill';

export default Facets;
